import React from 'react';
import { X, ExternalLink, AlertTriangle, Shield } from 'lucide-react';
import { getDomainFromUrl } from '@/utils/linkValidator';

interface ExternalLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    url: string;
}

const ExternalLinkModal: React.FC<ExternalLinkModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    url,
}) => {
    if (!isOpen) return null;

    const domain = getDomainFromUrl(url);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#790546] to-[#6a0440] p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                        aria-label="Cerrar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-3 rounded-full">
                            <AlertTriangle className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">
                                Sitio Externo
                            </h2>
                            <p className="text-white/90 text-sm">
                                Estás saliendo de PattyCarino.cl
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Main Warning */}
                    <div className="bg-[#790546]/5 dark:bg-[#790546]/10 border-2 border-[#790546]/20 rounded-xl p-4">
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                            Estás a punto de visitar un sitio web externo que{' '}
                            <strong className="text-[#790546] dark:text-[#790546]">
                                no está administrado por PattyCarino
                            </strong>
                            . Este sitio es parte de una colaboración comercial.
                        </p>
                    </div>

                    {/* Destination URL */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        <ExternalLink className="w-5 h-5 text-[#790546] dark:text-[#790546] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">
                                Serás redirigido a:
                            </p>
                            <p className="text-sm font-bold text-[#790546] dark:text-[#790546] break-all">
                                {domain}
                            </p>
                        </div>
                    </div>

                    {/* Legal Disclaimer */}
                    <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                        <Shield className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                <strong className="text-amber-700 dark:text-amber-500">Aviso importante:</strong>{' '}
                                PattyCarino no se hace responsable del contenido, políticas de privacidad,
                                términos de servicio ni de las acciones que ocurran en sitios externos.
                                Al continuar, aceptas que estás bajo tu propia responsabilidad.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-100 dark:hover:bg-gray-600 transition-all border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-[#790546] to-[#6a0440] text-white rounded-xl font-semibold hover:from-[#6a0440] hover:to-[#5a0338] transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        Continuar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExternalLinkModal;
